'use client';

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import type { StepConfig, FormValues, FormInstance, CustomErrorMessages } from '../types';
import {
  getNextStepIdFromConfig,
  getPreviousStepIdFromConfig,
  getNextVisibleStepIdInList,
  getPreviousVisibleStepIdInList,
} from '../utils/navigation';
import {
  findSectionForStep,
  findNextCardInSection,
  collectSectionFormData,
  clearCardFormData,
} from '../utils/sections';
import { getStepFields } from '../utils/steps';
import { validateField, scrollToFirstError } from '../utils/validation';
import { getVisibleFields } from '../utils/steps';
import { getAddMoreConfig, isAddMoreEnabled } from '../utils/addMoreConfig';
import { getFirstFieldIdFromCondition } from '../utils/conditions';
import { isCardConsideredCompleted } from '../utils/cardUtils';
import { WILLS_FORM_CONSTANTS } from 'helpers/Constants/SiteConfig';

import {
  shouldSaveCardBeforeNavigation,
  shouldSkipCardSaveForAddMore,
  saveCardInstance,
  clearFormInstanceTracking,
} from '../utils/navigationHelpers';
import { sendGTMEvent } from '@next/third-parties/google';
import { toolSectionCompleteGTMEvent } from 'lib/utils/gtm-utils';
import { addCompletedWillsStep } from '../utils/completedStepsStorage';

const resolveCardInstanceId = (
  card: import('../types').Card,
  completedForms: Record<string, import('../types').FormInstance[]>,
  currentFormInstanceId: string | null
): string => {
  if (currentFormInstanceId) return currentFormInstanceId;
  const instances = completedForms[card.groupKey] || [];
  if (instances.length > 0) {
    const sortedInstances = [...instances].sort((a, b) => {
      const aIndex = a.lastStepIndex ?? 0;
      const bIndex = b.lastStepIndex ?? 0;
      return bIndex - aIndex;
    });
    return sortedInstances[0].instanceId;
  }
  return `${card.id}-${Date.now()}`;
};

interface UseFormNavigationProps {
  allSteps: StepConfig[];
  visibleSteps: StepConfig[];
  stepsById: Record<string, StepConfig>;
  values: FormValues;
  setValues: React.Dispatch<React.SetStateAction<FormValues>>;
  errors: Record<string, string | undefined>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string | undefined>>>;
  touched: Record<string, boolean | undefined>;
  setTouched: React.Dispatch<React.SetStateAction<Record<string, boolean | undefined>>>;
  completedForms: Record<string, FormInstance[]>;
  setCompletedForms: React.Dispatch<React.SetStateAction<Record<string, FormInstance[]>>>;
  currentFormInstanceId: string | null;
  setCurrentFormInstanceId: React.Dispatch<React.SetStateAction<string | null>>;
  currentSectionId: string | null;
  setCurrentSectionId: React.Dispatch<React.SetStateAction<string | null>>;
  currentStepIndex: number;
  setCurrentStepIndex: React.Dispatch<React.SetStateAction<number>>;
  localStorageKey: string;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  setSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  initialStepId?: string;
  onStepIdChange?: (stepId: string) => void;
  customErrorMessages?: CustomErrorMessages;
}

interface UseFormNavigationReturn {
  currentStepId: string;
  setCurrentStepId: (stepId: string) => void;
  canGoBack: boolean;
  handleNext: (isSkip?: boolean) => Promise<void>;
  handleBack: () => void;
  validateCurrentStep: () => boolean;
}

/**
 * Get beneficiary groupKeys for the distribution step from the selected radio value.
 */
function getDistributionBeneficiaryGroupKeys(
  step: StepConfig,
  selectedValue: string | undefined
): string[] {
  if (!selectedValue) return [];
  for (const group of step.fieldGroups || []) {
    for (const f of group.fields || []) {
      if (f.id === WILLS_FORM_CONSTANTS.DISTRIBUTION_FIELD_ID && f.inlineCards) {
        const cardConfig = f.inlineCards[selectedValue];
        return cardConfig?.groupKeys ?? [];
      }
    }
  }
  return [];
}
/**
 * Custom hook for managing form navigation
 */
export function useFormNavigation({
  allSteps,
  visibleSteps,
  stepsById,
  values,
  setValues,
  setErrors,
  setTouched,
  completedForms,
  setCompletedForms,
  currentFormInstanceId,
  setCurrentFormInstanceId,
  currentSectionId,
  setCurrentSectionId,
  setCurrentStepIndex,
  localStorageKey,
  setIsSubmitting,
  setSubmitted,
  initialStepId,
  onStepIdChange,
  customErrorMessages,
}: UseFormNavigationProps): UseFormNavigationReturn {
  const [currentStepId, setCurrentStepIdState] = useState<string>(initialStepId || '');
  const trackedCompletedSectionIdsRef = useRef<Set<string>>(new Set());
  const editingSectionIdRef = useRef<string | null>(null);

  const getSectionNameTitle = (title: string) =>
    title
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');

  const trackSectionCompletedOnce = useCallback(
    (sectionId: string, sectionTitle: string, force?: boolean) => {
      if (!force && trackedCompletedSectionIdsRef.current.has(sectionId)) return;
      trackedCompletedSectionIdsRef.current.add(sectionId);
      const sectionIndex = allSteps
        .find((step) => step.id === 'step-3-sections')
        ?.sections?.findIndex((section) => section.id === sectionId);
      const gtmEvent = toolSectionCompleteGTMEvent({
        toolName: 'online_will',
        sectionName: `${(sectionIndex ?? 0) + 1}_${getSectionNameTitle(sectionTitle)}`,
        event: 'tool_section_complete',
      });
      sendGTMEvent(gtmEvent);
    },
    [allSteps]
  );

  // Initial step
  useEffect(() => {
    if (!currentStepId && allSteps.length > 0) {
      const firstStepId = allSteps[0].id;
      setCurrentStepIdState(firstStepId);
      onStepIdChange?.(firstStepId);
    }
  }, [allSteps, currentStepId, onStepIdChange]);

  // Track if the user is editing an existing section instance
  useEffect(() => {
    if (!currentSectionId || !currentFormInstanceId) {
      editingSectionIdRef.current = null;
      return;
    }
    const isExistingInstance = Object.values(completedForms).some((instances) =>
      instances.some((instance) => instance.instanceId === currentFormInstanceId)
    );
    if (isExistingInstance) {
      editingSectionIdRef.current = currentSectionId;
    }
  }, [completedForms, currentFormInstanceId, currentSectionId]);

  // Load persisted currentStepId
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(localStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed.currentStepId && stepsById[parsed.currentStepId]) {
        setCurrentStepIdState(parsed.currentStepId);
        onStepIdChange?.(parsed.currentStepId);
      }
    } catch (e) {
      console.error('Failed to read currentStepId from localStorage', e);
    }
  }, [localStorageKey, stepsById, onStepIdChange]);

  const currentStep = useMemo(() => {
    return stepsById[currentStepId] || visibleSteps[0] || allSteps[0];
  }, [currentStepId, stepsById, visibleSteps, allSteps]);

  useEffect(() => {
    const sectionInfo = findSectionForStep(currentStepId, allSteps);
    if (!sectionInfo?.card || !sectionInfo.cardId) return;
    if (currentSectionId && currentSectionId !== sectionInfo.sectionId) return;
    if (currentFormInstanceId) return;
    const instanceId = resolveCardInstanceId(
      sectionInfo.card,
      completedForms,
      currentFormInstanceId
    );
    setCurrentFormInstanceId(instanceId);
    if (!currentSectionId) {
      setCurrentSectionId(sectionInfo.sectionId);
    }
  }, [
    allSteps,
    completedForms,
    currentFormInstanceId,
    currentSectionId,
    currentStepId,
    setCurrentFormInstanceId,
    setCurrentSectionId,
  ]);

  const isLastStep = useMemo(() => {
    // Check if next step type is submit
    if (currentStep.next?.type === 'submit') return true;
    // Check if next step ID is 'step-9-summary'
    const nextStepId = getNextStepIdFromConfig(currentStep, values);
    if (nextStepId === 'step-10-thankyou') return true;
    return false;
  }, [currentStep, values]);

  // Check if we can go back
  const canGoBack = useMemo(() => {
    const actualCurrentStep = stepsById[currentStepId];
    if (!actualCurrentStep) return false;

    const previousFromConfig = getPreviousStepIdFromConfig(actualCurrentStep, values);
    if (previousFromConfig && stepsById[previousFromConfig]) {
      return true;
    }
    const previousFromList = getPreviousVisibleStepIdInList(visibleSteps, currentStepId);
    return previousFromList !== null;
  }, [currentStepId, values, visibleSteps, stepsById]);

  // Validate current step
  const validateCurrentStep = useCallback((): boolean => {
    if (!currentStep) return false;

    const visibleFields = currentStep.fieldGroups ? getVisibleFields(currentStep, values) : [];
    const newErrors: Record<string, string | undefined> = {};
    const newTouched: Record<string, boolean | undefined> = {};

    // Generic handling for addMore steps: skip validation of empty addMore fields
    // AddMore fields should only be validated when the form is visible and being filled
    const addMoreConfig = getAddMoreConfig(currentStep);
    const areAddMoreFieldsEmpty = addMoreConfig
      ? addMoreConfig.fieldIds.every((fieldId) => !values[fieldId] || values[fieldId] === '')
      : false;

    // Validate all visible fields in the current step
    for (const field of visibleFields) {
      // Skip validation of addMore fields if they're empty (form is hidden after adding)
      // This allows navigation when addMore instances have been added and form is hidden
      if (addMoreConfig && areAddMoreFieldsEmpty && addMoreConfig.fieldIds.includes(field.id)) {
        continue; // Skip validation for empty addMore fields
      }

      const value = values[field.id];
      const error = validateField(field, value, {
        completedForms,
        allValues: values,
        editingInstanceId: currentFormInstanceId,
        addMoreConfig: addMoreConfig ?? undefined,
        localStorageKey,
        customErrorMessages,
      });
      if (error) {
        newErrors[field.id] = error;
        // Mark field as touched so error displays
        newTouched[field.id] = true;
      }
    }

    setErrors(newErrors);
    setTouched((prev) => ({ ...prev, ...newTouched }));
    // Scroll to first error if any errors found
    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError(newErrors);
    }

    return Object.keys(newErrors).length === 0;
  }, [
    currentStep,
    values,
    setErrors,
    setTouched,
    completedForms,
    currentFormInstanceId,
    localStorageKey,
    customErrorMessages,
  ]);

  // Handle next navigation
  const handleNext = useCallback(
    async (isSkip?: boolean) => {
      // Validate current step before proceeding - block navigation if validation fails
      if (!validateCurrentStep()) return;

      if (!currentStep) return;

      // Special handling: If skip is clicked on guardian step, mark it as skipped
      if (
        isSkip &&
        (currentStep.id === 'step-4-6-guardian' || currentStep.id === 'step-5-5-guardian')
      ) {
        // Set a form value to indicate guardian was skipped
        // This will be checked by considerCompletedIf to mark the card as completed
        const skipFieldName =
          currentStep.id === 'step-4-6-guardian'
            ? 'guardianSkipped'
            : 'spousePartnerGuardianSkipped';
        setValues((prev) => ({
          ...prev,
          [skipFieldName]: true,
        }));
      }

      // Generic validation: If addMore is enabled but no instances exist, prevent navigation
      const addMoreConfig = getAddMoreConfig(currentStep);
      if (addMoreConfig) {
        const isEnabled = isAddMoreEnabled(currentStep, values);
        if (isEnabled) {
          const instances = completedForms?.[addMoreConfig.groupKey] || [];
          if (instances.length === 0 && !isSkip) {
            // Get the field ID from enableCondition to show error on that field
            const errorFieldId = getFirstFieldIdFromCondition(addMoreConfig.enableCondition);
            if (errorFieldId) {
              setErrors((prev) => ({
                ...prev,
                [errorFieldId]: `Please add at least one ${addMoreConfig.cardTitle.toLowerCase()} before proceeding.`,
              }));
              setTouched((prev) => ({
                ...prev,
                [errorFieldId]: true,
              }));
              scrollToFirstError({
                [errorFieldId]: `Please add at least one ${addMoreConfig.cardTitle.toLowerCase()} before proceeding.`,
              });
            }
            return;
          }
        }
      }

      // Distribution estate: require at least one beneficiary (spouse, children, charity, cash-gift, etc.)
      if (currentStep.id === WILLS_FORM_CONSTANTS.DISTRIBUTION_STEP_ID && !isSkip) {
        const selectedValue = values[WILLS_FORM_CONSTANTS.DISTRIBUTION_FIELD_ID];
        const groupKeys = getDistributionBeneficiaryGroupKeys(currentStep, selectedValue);
        const totalBeneficiaries = groupKeys.reduce(
          (sum, key) => sum + (completedForms?.[key]?.length ?? 0),
          0
        );
        if (totalBeneficiaries === 0) {
          setErrors((prev) => ({
            ...prev,
            [WILLS_FORM_CONSTANTS.DISTRIBUTION_FIELD_ID]:
              WILLS_FORM_CONSTANTS.DISTRIBUTION_ERROR_MESSAGE,
          }));
          setTouched((prev) => ({ ...prev, [WILLS_FORM_CONSTANTS.DISTRIBUTION_FIELD_ID]: true }));
          scrollToFirstError({
            [WILLS_FORM_CONSTANTS.DISTRIBUTION_FIELD_ID]:
              WILLS_FORM_CONSTANTS.DISTRIBUTION_ERROR_MESSAGE,
          });
          return;
        }
        setErrors((prev) => {
          const next = { ...prev };
          if (next[WILLS_FORM_CONSTANTS.DISTRIBUTION_FIELD_ID]) {
            delete next[WILLS_FORM_CONSTANTS.DISTRIBUTION_FIELD_ID];
          }
          return next;
        });
      }

      addCompletedWillsStep(currentStep.id);

      if (isLastStep) {
        try {
          setIsSubmitting(true);
          setSubmitted(true);
        } catch (e) {
          console.error('Failed to submit form', e);
        } finally {
          setIsSubmitting(false);
        }
        return;
      }

      if (!currentStep) return;

      // Check if current step is a section/card form step (not the section list step)
      const sectionInfo = findSectionForStep(currentStep.id, allSteps);
      const isInSection =
        sectionInfo && (currentSectionId === sectionInfo.sectionId || !currentSectionId);
      if (isInSection && sectionInfo) {
        if (!currentSectionId) {
          setCurrentSectionId(sectionInfo.sectionId);
        }
        const instanceIdForCard =
          sectionInfo.card && sectionInfo.cardId
            ? resolveCardInstanceId(sectionInfo.card, completedForms, currentFormInstanceId)
            : currentFormInstanceId;
        if (sectionInfo.card && sectionInfo.cardId && !currentFormInstanceId && instanceIdForCard) {
          setCurrentFormInstanceId(instanceIdForCard);
        }
        const isEditingSection = editingSectionIdRef.current === sectionInfo.sectionId;
        // This is a section/card form step - collect form data for this step
        const stepFields = getStepFields(currentStep);
        const stepFormData: FormValues = {};
        stepFields.forEach((field) => {
          if (values[field.id] !== undefined) {
            stepFormData[field.id] = values[field.id];
          }
        });

        // Check if it's a card-based section
        if (sectionInfo.card && sectionInfo.cardId) {
          // Card-based section
          // FIRST: Check if step has a next configuration to navigate to
          // This takes precedence over card's stepIds array
          const stepNextFromConfig = getNextStepIdFromConfig(currentStep, values);
          if (stepNextFromConfig && stepNextFromConfig !== currentStep.id) {
            // Step has a next configuration - navigate there directly
            // Check if the next step belongs to a different card or is not in any card
            const nextStepInfo = findSectionForStep(stepNextFromConfig, allSteps);

            // Check if card should be saved before navigation
            const shouldSaveCard = shouldSaveCardBeforeNavigation(sectionInfo, nextStepInfo);

            if (shouldSaveCard && sectionInfo.card) {
              // Check if card save should be skipped for addMore steps
              const shouldSkipCardSave = shouldSkipCardSaveForAddMore(
                currentStep,
                sectionInfo,
                values
              );

              // Check if card should be considered completed (e.g., hasBusiness === "No")
              // If so, don't save the card - it's already considered completed
              const isCardCompleted = sectionInfo.card
                ? isCardConsideredCompleted(sectionInfo.card, values)
                : false;

              // Only save card instance if:
              // 1. Not skipping (for addMore steps with empty fields), AND
              // 2. Card is not considered completed via considerCompletedIf, AND
              // 3. We have a current form instance ID
              if (!shouldSkipCardSave && !isCardCompleted && instanceIdForCard) {
                saveCardInstance(
                  sectionInfo,
                  instanceIdForCard,
                  currentStep,
                  stepsById,
                  values,
                  setCompletedForms
                );
              }

              // If next step is in a different card, set up for that card
              if (
                nextStepInfo &&
                nextStepInfo.card &&
                nextStepInfo.card.id !== sectionInfo.card?.id
              ) {
                if (nextStepInfo.sectionId !== sectionInfo.sectionId) {
                  trackSectionCompletedOnce(
                    sectionInfo.sectionId,
                    sectionInfo.section.title,
                    isEditingSection
                  );
                }
                // Check if the next card shares the same groupKey
                // If so, reuse the existing instanceId to merge data instead of creating a new instance
                const sharesGroupKey =
                  sectionInfo.card && nextStepInfo.card.groupKey === sectionInfo.card.groupKey;

                let newInstanceId: string;
                if (sharesGroupKey && instanceIdForCard) {
                  // Reuse existing instanceId to merge data
                  newInstanceId = instanceIdForCard;
                } else {
                  // Check if there's an existing instance for the next card's groupKey
                  // This handles the case where we're returning to a card we've already been to
                  const nextCardInstances = completedForms[nextStepInfo.card.groupKey] || [];
                  if (nextCardInstances.length > 0) {
                    // Reuse the most recent instance for this groupKey
                    // Sort by lastStepIndex (or use the first one if no lastStepIndex)
                    const sortedInstances = [...nextCardInstances].sort((a, b) => {
                      const aIndex = a.lastStepIndex ?? 0;
                      const bIndex = b.lastStepIndex ?? 0;
                      return bIndex - aIndex; // Most recent first
                    });
                    newInstanceId = sortedInstances[0].instanceId;
                  } else {
                    // Create new instanceId for different groupKey
                    newInstanceId = `${nextStepInfo.card.id}-${Date.now()}`;
                  }
                }

                setCurrentFormInstanceId(newInstanceId);
                setCurrentSectionId(nextStepInfo.sectionId);
                setCurrentStepIndex(0);

                // Clear form values for the new card, then restore any saved instance data for
                // this card (same as findNextCardInSection). Step-level next config (e.g. 8-1 → 8-3
                // when hasCharities is Yes) skips the sequential card path, so without this merge
                // fields like hasCashGiftToSomeone are wiped even though they exist in completedForms.
                const clearedValues = clearCardFormData(nextStepInfo.card, stepsById, values);
                const loadedValues = { ...clearedValues };
                const savedForNextCard = (completedForms[nextStepInfo.card.groupKey] || []).find(
                  (inst) => inst.instanceId === newInstanceId
                );
                const nextStepConfig = stepsById[stepNextFromConfig];
                const nextAddMoreConfig = nextStepConfig ? getAddMoreConfig(nextStepConfig) : null;
                const excludedFieldIds = new Set(
                  nextAddMoreConfig?.groupKey === nextStepInfo.card.groupKey
                    ? nextAddMoreConfig.fieldIds
                    : []
                );
                if (savedForNextCard) {
                  Object.keys(savedForNextCard.data).forEach((fieldId) => {
                    if (excludedFieldIds.has(fieldId)) return;
                    if (savedForNextCard.data[fieldId] !== undefined) {
                      loadedValues[fieldId] = savedForNextCard.data[fieldId];
                    }
                  });
                }
                setValues(loadedValues);
                setErrors({});
                setTouched({});
              } else {
                // Next step is not in a card (e.g., section list) - clear form instance tracking
                clearFormInstanceTracking({
                  setCurrentFormInstanceId,
                  setCurrentSectionId,
                  setCurrentStepIndex,
                });
                trackSectionCompletedOnce(
                  sectionInfo.sectionId,
                  sectionInfo.section.title,
                  isEditingSection
                );
              }
            }

            setCurrentStepIdState(stepNextFromConfig);
            onStepIdChange?.(stepNextFromConfig);
            return;
          }

          // THEN: Check if there's a next step in the card
          const nextStepIndex = sectionInfo.stepIndex + 1;
          if (nextStepIndex < sectionInfo.card.stepIds.length) {
            // Save current card before navigating to next step within the same card
            // This ensures data from non-addMore steps (e.g. estateDistributionPreference, card-other-information) is persisted
            const shouldSkipCardSave = shouldSkipCardSaveForAddMore(
              currentStep,
              sectionInfo,
              values
            );
            const isCardCompleted = sectionInfo.card
              ? isCardConsideredCompleted(sectionInfo.card, values)
              : false;
            if (!shouldSkipCardSave && !isCardCompleted && instanceIdForCard) {
              saveCardInstance(
                sectionInfo,
                instanceIdForCard,
                currentStep,
                stepsById,
                values,
                setCompletedForms
              );
            }

            // Navigate to next step in the card
            const nextStepId = sectionInfo.card.stepIds[nextStepIndex];
            setCurrentStepIndex(nextStepIndex);
            setCurrentStepIdState(nextStepId);
            return;
          }

          // No more steps in the card - save the card instance first
          // This ensures the card data is persisted even if there's a next step configured
          // BUT: Skip saving for addMore steps if form fields are empty (form is hidden after "Add")
          // Check if card save should be skipped for addMore steps
          const shouldSkipCardSave = shouldSkipCardSaveForAddMore(currentStep, sectionInfo, values);

          // Check if card should be considered completed (e.g., hasBusiness === "No")
          // If so, don't save the card - it's already considered completed
          const isCardCompleted = sectionInfo.card
            ? isCardConsideredCompleted(sectionInfo.card, values)
            : false;

          // Only save card instance if:
          // 1. Not skipping (for addMore steps with empty fields), AND
          // 2. Card is not considered completed via considerCompletedIf, AND
          // 3. We have a current form instance ID
          if (!shouldSkipCardSave && !isCardCompleted && sectionInfo.card) {
            const instanceIdToSave = resolveCardInstanceId(
              sectionInfo.card,
              completedForms,
              currentFormInstanceId
            );
            saveCardInstance(
              sectionInfo,
              instanceIdToSave,
              currentStep,
              stepsById,
              values,
              setCompletedForms
            );
          }
          // Check if there's a next card in the section
          const nextCard = findNextCardInSection(sectionInfo.section, sectionInfo.card.id);
          if (nextCard && nextCard.stepIds && nextCard.stepIds.length > 0) {
            const firstStepId = nextCard.stepIds[0];

            // If we're returning to a card that already has saved data - reuse instance and load data
            const nextCardInstances = completedForms[nextCard.groupKey] || [];
            if (nextCardInstances.length > 0) {
              const sortedInstances = [...nextCardInstances].sort((a, b) => {
                const aIndex = a.lastStepIndex ?? 0;
                const bIndex = b.lastStepIndex ?? 0;
                return bIndex - aIndex; // Most recent first
              });
              const existingInstance = sortedInstances[0];
              setCurrentFormInstanceId(existingInstance.instanceId);
              setCurrentSectionId(sectionInfo.sectionId);
              setCurrentStepIndex(0);
              const clearedValues = clearCardFormData(nextCard, stepsById, values);
              const loadedValues = { ...clearedValues };
              const targetStep = stepsById[firstStepId];
              const targetAddMoreConfig = targetStep ? getAddMoreConfig(targetStep) : null;
              const excludedFieldIds = new Set(
                targetAddMoreConfig?.groupKey === nextCard.groupKey
                  ? targetAddMoreConfig.fieldIds
                  : []
              );
              Object.keys(existingInstance.data).forEach((fieldId) => {
                if (excludedFieldIds.has(fieldId)) return;
                if (existingInstance.data[fieldId] !== undefined) {
                  loadedValues[fieldId] = existingInstance.data[fieldId];
                }
              });
              setValues(loadedValues);
              setErrors({});
              setTouched({});
              setCurrentStepIdState(firstStepId);
              return;
            }

            // New card - create new instance and clear form
            const newInstanceId = `${nextCard.id}-${Date.now()}`;
            setCurrentFormInstanceId(newInstanceId);
            setCurrentSectionId(sectionInfo.sectionId);
            setCurrentStepIndex(0);

            const clearedValues = clearCardFormData(nextCard, stepsById, values);
            setValues(clearedValues);
            setErrors({});
            setTouched({});

            setCurrentStepIdState(firstStepId);
            return;
          }

          // No next card - clear current form instance tracking and navigate back to section list
          clearFormInstanceTracking({
            setCurrentFormInstanceId,
            setCurrentSectionId,
            setCurrentStepIndex,
          });

          trackSectionCompletedOnce(
            sectionInfo.sectionId,
            sectionInfo.section.title,
            isEditingSection
          );

          // Check if next step is configured and if it's the section list step
          const nextFromConfig = getNextStepIdFromConfig(currentStep, values);
          const sectionListStep = allSteps.find((s) =>
            s.sections?.some((sec) => sec.id === sectionInfo.sectionId)
          );

          // Only navigate back to section list if:
          // 1. Next step is explicitly the section list step, OR
          // 2. No next step is configured (default behavior for card forms)
          if (sectionListStep && (nextFromConfig === sectionListStep.id || !nextFromConfig)) {
            setCurrentStepIdState(sectionListStep.id);
            onStepIdChange?.(sectionListStep.id);
            return;
          }
          // Otherwise, continue with normal next step navigation (use nextFromConfig)
          if (nextFromConfig) {
            setCurrentStepIdState(nextFromConfig);
            onStepIdChange?.(nextFromConfig);
            return;
          }
        } else {
          // Legacy: Section without cards
          // Check if there's a next stepId in the section's stepIds array
          const nextStepIndex = sectionInfo.stepIndex + 1;
          if (sectionInfo.section.stepIds && nextStepIndex < sectionInfo.section.stepIds.length) {
            // There's a next step in the section - navigate to it
            const nextStepId = sectionInfo.section.stepIds[nextStepIndex];
            setCurrentStepIndex(nextStepIndex);
            setCurrentStepIdState(nextStepId);
            return;
          }

          // No more steps in the section - save as completed instance
          const allFormData = collectSectionFormData(sectionInfo.section, stepsById, values);

          // Determine the key to use for grouping (use groupKey if available, otherwise use sectionId)
          const groupKey = sectionInfo.section.groupKey || sectionInfo.sectionId;

          const instanceId = currentFormInstanceId ?? `${sectionInfo.sectionId}-${Date.now()}`;

          // Save completed form instance
          setCompletedForms((prev) => {
            const sectionInstances = prev[groupKey] || [];
            const existingIndex = sectionInstances.findIndex(
              (inst) => inst.instanceId === instanceId
            );

            const newInstance = {
              instanceId,
              data: allFormData,
            };

            if (existingIndex >= 0) {
              // Update existing instance
              const updated = [...sectionInstances];
              updated[existingIndex] = newInstance;
              return { ...prev, [groupKey]: updated };
            } else {
              // Add new instance
              return { ...prev, [groupKey]: [...sectionInstances, newInstance] };
            }
          });

          // Clear current form instance tracking
          setCurrentFormInstanceId(null);
          setCurrentSectionId(null);
          setCurrentStepIndex(0);

          trackSectionCompletedOnce(
            sectionInfo.sectionId,
            sectionInfo.section.title,
            isEditingSection
          );

          // Check if next step is configured and if it's the section list step
          const nextFromConfig = getNextStepIdFromConfig(currentStep, values);
          const sectionListStep = allSteps.find((s) =>
            s.sections?.some((sec) => sec.id === sectionInfo.sectionId)
          );

          // Only navigate back to section list if:
          // 1. Next step is explicitly the section list step, OR
          // 2. No next step is configured (default behavior for section forms)
          if (sectionListStep && (nextFromConfig === sectionListStep.id || !nextFromConfig)) {
            setCurrentStepIdState(sectionListStep.id);
            onStepIdChange?.(sectionListStep.id);
            return;
          }
          // Otherwise, continue with normal next step navigation (use nextFromConfig)
          if (nextFromConfig) {
            setCurrentStepIdState(nextFromConfig);
            onStepIdChange?.(nextFromConfig);
            return;
          }
        }
      }

      const nextFromConfig = getNextStepIdFromConfig(currentStep, values);
      let targetStepId: string | null = nextFromConfig;

      // If config target is not visible, fall back to next visible step
      if (!targetStepId || !visibleSteps.some((s) => s.id === targetStepId)) {
        targetStepId = getNextVisibleStepIdInList(visibleSteps, currentStep.id);
      }

      if (!targetStepId) {
        setSubmitted(true);
        return;
      }

      setCurrentStepIdState(targetStepId);
    },
    [
      validateCurrentStep,
      isLastStep,
      currentStep,
      allSteps,
      currentFormInstanceId,
      currentSectionId,
      values,
      stepsById,
      visibleSteps,
      completedForms,
      setCompletedForms,
      setCurrentFormInstanceId,
      setCurrentSectionId,
      setCurrentStepIndex,
      setIsSubmitting,
      setSubmitted,
      onStepIdChange,
      setErrors,
      setTouched,
      setValues,
      trackSectionCompletedOnce,
    ]
  );

  // Handle back navigation
  const handleBack = useCallback(() => {
    // Back navigation does NOT require validation - users should be able to go back
    // even with invalid data to review or change previous answers

    // CRITICAL: Use stepsById to get the actual current step, not currentStep from visibleSteps
    // currentStep might be wrong if the step has showIf conditions
    const actualCurrentStep = stepsById[currentStepId];
    if (!actualCurrentStep) {
      console.error('Current step not found in stepsById:', currentStepId);
      return;
    }

    // Check if we're in a section/card form
    const sectionInfo = findSectionForStep(currentStepId, allSteps);
    if (sectionInfo && currentSectionId === sectionInfo.sectionId) {
      // Check if it's a card-based section
      if (sectionInfo.card && sectionInfo.cardId) {
        // Card-based section
        const previousStepIndex = sectionInfo.stepIndex - 1;
        if (previousStepIndex >= 0) {
          // There's a previous step in the card - navigate to it
          const previousStepId = sectionInfo.card.stepIds[previousStepIndex];
          setCurrentStepIndex(previousStepIndex);
          setCurrentStepIdState(previousStepId);
          return;
        }
        // If it's the first step of a card, check for configured back step first
        // This handles cases where steps from different cards are linked (e.g., step-7-2 -> step-7-3)
        const previousFromConfig = getPreviousStepIdFromConfig(actualCurrentStep, values);
        if (previousFromConfig && stepsById[previousFromConfig]) {
          // Check if the configured back step is in a different card
          const backStepInfo = findSectionForStep(previousFromConfig, allSteps);
          if (backStepInfo && backStepInfo.cardId !== sectionInfo.cardId) {
            const shouldSkipCardSave = shouldSkipCardSaveForAddMore(
              actualCurrentStep,
              sectionInfo,
              values
            );
            const isCardCompleted = sectionInfo.card
              ? isCardConsideredCompleted(sectionInfo.card, values)
              : false;
            if (!shouldSkipCardSave && !isCardCompleted && sectionInfo.card) {
              const instanceIdToSave = resolveCardInstanceId(
                sectionInfo.card,
                completedForms,
                currentFormInstanceId
              );
              saveCardInstance(
                sectionInfo,
                instanceIdToSave,
                actualCurrentStep,
                stepsById,
                values,
                setCompletedForms
              );
            }
            // Back step is in a different card - clear card tracking and use configured back step
            clearFormInstanceTracking({
              setCurrentFormInstanceId,
              setCurrentSectionId,
              setCurrentStepIndex,
            });
          }
          // Use configured back step instead of jumping to section list
          setCurrentStepIdState(previousFromConfig);
          return;
        }
        // If it's the first step of a card, go back to the section list
        const sectionListStep = allSteps.find((s) =>
          s.sections?.some((sec) => sec.id === sectionInfo.sectionId)
        );
        if (sectionListStep) {
          const shouldSkipCardSave = shouldSkipCardSaveForAddMore(
            actualCurrentStep,
            sectionInfo,
            values
          );
          const isCardCompleted = sectionInfo.card
            ? isCardConsideredCompleted(sectionInfo.card, values)
            : false;
          if (!shouldSkipCardSave && !isCardCompleted && sectionInfo.card) {
            const instanceIdToSave = resolveCardInstanceId(
              sectionInfo.card,
              completedForms,
              currentFormInstanceId
            );
            saveCardInstance(
              sectionInfo,
              instanceIdToSave,
              actualCurrentStep,
              stepsById,
              values,
              setCompletedForms
            );
          }
          setCurrentStepIdState(sectionListStep.id);
          setCurrentFormInstanceId(null);
          setCurrentSectionId(null);
          setCurrentStepIndex(0);
          return;
        }
      } else {
        // Legacy: Section without cards
        const previousStepIndex = sectionInfo.stepIndex - 1;
        if (
          sectionInfo.section.stepIds &&
          previousStepIndex >= 0 &&
          previousStepIndex < sectionInfo.section.stepIds.length
        ) {
          // There's a previous step in the section - navigate to it
          const previousStepId = sectionInfo.section.stepIds[previousStepIndex];
          setCurrentStepIndex(previousStepIndex);
          setCurrentStepIdState(previousStepId);
          return;
        }
        // If it's the first step of a section, check for configured back step first
        const previousFromConfig = getPreviousStepIdFromConfig(actualCurrentStep, values);
        if (previousFromConfig && stepsById[previousFromConfig]) {
          setCurrentStepIdState(previousFromConfig);
          return;
        }
        const sectionListStep = allSteps.find((s) =>
          s.sections?.some((sec) => sec.id === sectionInfo.sectionId)
        );
        if (sectionListStep) {
          setCurrentStepIdState(sectionListStep.id);
          setCurrentFormInstanceId(null);
          setCurrentSectionId(null);
          setCurrentStepIndex(0);
          return;
        }
      }
    }

    const previousFromConfig = getPreviousStepIdFromConfig(actualCurrentStep, values);
    let targetStepId: string | null = previousFromConfig;

    // If we have a configured back step, check if it exists in allSteps
    if (targetStepId && stepsById[targetStepId]) {
      // Allow navigation to configured back step even if not currently visible
      setCurrentStepIdState(targetStepId);
      return;
    }

    // If no config or config target doesn't exist, fall back to previous visible step
    targetStepId = getPreviousVisibleStepIdInList(visibleSteps, currentStepId);
    if (!targetStepId) {
      // Nothing to go back to
      return;
    }

    setCurrentStepIdState(targetStepId);
  }, [
    currentStepId,
    currentSectionId,
    values,
    visibleSteps,
    stepsById,
    allSteps,
    completedForms,
    currentFormInstanceId,
    setCurrentFormInstanceId,
    setCurrentSectionId,
    setCurrentStepIndex,
    setCompletedForms,
    // Note: onStepIdChange, setErrors, setTouched, setValues are stable and don't need to be in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  const setCurrentStepId = useCallback(
    (stepId: string) => {
      setCurrentStepIdState(stepId);
      onStepIdChange?.(stepId);
    },
    [onStepIdChange]
  );

  return {
    currentStepId,
    setCurrentStepId,
    canGoBack,
    handleNext,
    handleBack,
    validateCurrentStep,
  };
}
